import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight, Star } from "lucide-react";
import { STANDARD_PACKAGES } from "../mock";

export default function PackagesGrid({ withHeader = true }) {
  return (
    <section className="relative bg-[var(--lc-cream-soft)] py-20 lg:py-28">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        {withHeader && (
          <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-20">
            <div className="eyebrow text-[var(--lc-moss)] mb-5">Standard Packages</div>
            <h2 className="font-serif-display text-[clamp(36px,5.5vw,76px)] leading-[1] font-light">
              Three plans. <span className="italic">One philosophy.</span>
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[var(--lc-muted)]">
              Choose a ready-made plan or build your own. Every package is 100%
              flexible — we'll modify the pillars to align with your goals and
              schedule.
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-5 lg:gap-6">
          {STANDARD_PACKAGES.map((pkg) => (
            <article
              key={pkg.id}
              className={`relative rounded-lg p-7 lg:p-10 flex flex-col ${
                pkg.accent
                  ? "bg-[var(--lc-ink)] text-[var(--lc-cream)]"
                  : "bg-white text-[var(--lc-ink)] border border-[var(--lc-line)]"
              }`}
            >
              {pkg.accent && (
                <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--lc-moss)] text-white text-[10px] font-bold tracking-[0.2em] uppercase rounded-sm">
                  <Star className="h-3 w-3 fill-white" /> Most Popular
                </div>
              )}
              <div className="flex items-baseline justify-between mb-3">
                <span className={`eyebrow ${pkg.accent ? "text-[var(--lc-sand)]" : "text-[var(--lc-muted)]"}`}>
                  Tier {pkg.tier}
                </span>
                <span className={`text-[11px] ${pkg.accent ? "text-white/60" : "text-[var(--lc-muted)]"}`}>
                  Best for {pkg.bestFor}
                </span>
              </div>
              <h3 className="font-serif-display text-[40px] lg:text-[52px] leading-none font-light">
                {pkg.name}
              </h3>
              <p className={`mt-4 text-[15px] font-semibold ${pkg.accent ? "text-[var(--lc-sand)]" : "text-[var(--lc-moss)]"}`}>
                {pkg.tagline}
              </p>
              <p className={`mt-2 text-[14px] leading-relaxed ${pkg.accent ? "text-white/75" : "text-[var(--lc-muted)]"}`}>
                {pkg.description}
              </p>

              <ul className="mt-7 space-y-2.5 flex-1">
                {pkg.includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-2.5 text-[14px]">
                    <span
                      className={`mt-0.5 grid place-items-center w-4 h-4 rounded-full shrink-0 ${
                        pkg.accent ? "bg-[var(--lc-moss)] text-white" : "bg-[var(--lc-moss)] text-white"
                      }`}
                    >
                      <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
                    </span>
                    <span className="leading-snug">{inc}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 pt-6 border-t border-current/10" style={{ borderColor: pkg.accent ? "rgba(255,255,255,0.12)" : "var(--lc-line)" }}>
                <div className="mb-5">
                  <div className={`text-[11px] tracking-[0.18em] uppercase mb-2 ${pkg.accent ? "text-[var(--lc-sand)]" : "text-[var(--lc-muted)]"}`}>
                    Investment
                  </div>
                  <div className={`font-serif-display text-2xl lg:text-3xl leading-tight ${pkg.accent ? "text-white" : "text-[var(--lc-ink)]"}`}>
                    Reach out to <span className="italic">discuss.</span>
                  </div>
                  <div className={`text-[12px] mt-2 ${pkg.accent ? "text-white/60" : "text-[var(--lc-muted)]"}`}>
                    Tailored quote sent within 24 hours.
                  </div>
                </div>
                <Link
                  to={`/book?package=${pkg.id}`}
                  className={`group flex items-center justify-center gap-2 w-full px-5 py-3.5 text-[12px] font-bold tracking-[0.18em] uppercase transition-colors ${
                    pkg.accent
                      ? "bg-[var(--lc-moss)] text-white hover:bg-[var(--lc-sand)] hover:text-[var(--lc-ink)]"
                      : "bg-[var(--lc-ink)] text-[var(--lc-cream)] hover:bg-[var(--lc-moss-dark)]"
                  }`}
                >
                  Start with {pkg.name}
                  <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-[var(--lc-muted)] mb-4">
            Want something different? Mix services across all six pillars.
          </p>
          <Link
            to="/build"
            className="inline-flex items-center gap-2 px-6 py-3 border border-[var(--lc-ink)] text-[var(--lc-ink)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-ink)] hover:text-[var(--lc-cream)] transition-colors"
          >
            Build Your Custom Plan <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
